import { useState, useContext, useEffect } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { CampaignContext, CampaignState } from '../../context/campaign';
import Select, {
    type DropdownIndicatorProps,
    ClearIndicatorProps,
    components,
} from 'react-select';
import {
    searchMedicalCenters,
    searchMedicalProfessionals,
    setMedicalProfessional,
    fetchCampaignContext,
} from '~/services/registration';
import states from '~/shared/states.json';
import { CheckBox } from '~/shared/components/form/CheckBox';
import {
    Button,
    Col,
    Row,
    Form,
    FormGroup,
    Input,
    Label,
    Spinner,
    FormFeedback,
} from 'reactstrap';
import { SelectOption } from '~/types/globals';
import { Loading } from '~/shared/components/Loading';
import {
    MedicalCenter,
    MedicalProfessional as typeMedicalProfessional,
} from '~/types/campaign';
import { MedCenterForm } from './components/MedCenterForm';
import { identityOptions } from '~/shared/config';
import moment from 'moment';
import { AdminContext } from '~/context/admin';
import {
    colourStyles,
    DropdownIndicator,
    stateOptions,
} from '~/shared/components/form/Inputs';
import toast from 'react-hot-toast';

export const MedicalProfessional = ({}: any) => {
    const [{ campaign }, campaignDispatch]: [CampaignState, any] =
        useContext(CampaignContext);
    const [{ isAdmin, campaignId }, adminDispatch] = useContext(AdminContext);
    const [state, setState] = useState('');
    const [selectedMedicalCenter, setSelectedMedicalCenter] = useState<
        SelectOption | undefined | null
    >();
    const [medicalCenterOptions, setMedicalCenterOptions] = useState<
        SelectOption[]
    >([]);
    const [selectedMedicalProfessional, setSelectedMedicalProfessional] =
        useState<SelectOption | undefined | null>();
    const [medicalProfessionalOptions, setMedicalProfessionalOptions] =
        useState<SelectOption[]>([]);
    const today = moment().format('Y-MM-DD');
    const [loadingOptions, setLoadingOptions] = useState<boolean>(true);
    const [loading, setLoading] = useState<boolean>(true);
    const [saving, setSaving] = useState<boolean>(false);
    const [readOnly, setReadOnly] = useState(false);
    const [consent, setConsent] = useState<boolean>(false);
    const [certifyIdentity, setCertifyIdentity] = useState<number | null>(null);
    const [signature, setSignature] = useState<string>('');
    const history = useHistory();
    const location = useLocation<{ read_only: boolean }>();
    const [showMedCenterForm, setShowMedCenterForm] = useState<boolean>(false);
    const [showMedProfForm, setShowMedProfForm] = useState<boolean>(false);

    const getCampaign = async () => {
        const resp = await fetchCampaignContext();
        if (resp['detail'] === 'Invalid token.') {
            history.push('/login');
        } else {
            campaignDispatch({
                type: 'set_campaign',
                payload: resp,
            });
        }
    };

    useEffect(() => {
        if (location.state) {
            const { read_only } = location.state;
            if (read_only) {
                setReadOnly(read_only);
            }
        }
    }, [location]);

    const initializeData = async (medProf: typeMedicalProfessional) => {
        if (!medProf) {
            setLoading(false);
        } else {
            const medCenter = medProf.medical_center;
            if (medCenter) {
                setState(medCenter.location);
            }
            const medCenterResp = await searchMedicalCenters(
                medCenter.location
            );
            const medCenterOpts = medCenterResp.map(
                (medCenter: MedicalCenter) => {
                    return {
                        label: medCenter.name,
                        value: medCenter.id,
                    };
                }
            );
            const selectedMedCenterOpt = medCenterOpts.find(
                (x: { value: number }) => x.value === medCenter.id
            );
            const medProfsResp = await searchMedicalProfessionals(medCenter.id);
            const medProfsOpts = medProfsResp.map((prof: any) => {
                const label = `${prof.first_name} ${prof.last_name}`;
                return {
                    label,
                    value: prof.id,
                };
            });
            const selectedMedProfOpt = medProfsOpts.find(
                (x: { value: number }) => x.value === medProf.person.id
            );
            setMedicalCenterOptions(medCenterOpts);
            setMedicalProfessionalOptions(medProfsOpts);
            setSelectedMedicalCenter(selectedMedCenterOpt);
            setState(medCenter.location);
            setSelectedMedicalProfessional(selectedMedProfOpt);
            setLoading(false);
            setLoadingOptions(false);
        }
    };

    useEffect(() => {
        if (campaign) {
            const medProf = campaign?.patient?.medical_professional;

            if (medProf) {
                const consent =
                    campaign?.campaignApplication
                        ?.mp_give_consent_for_mp_to_provide_info || false;
                const certifyIdentity =
                    campaign?.campaignApplication?.mp_certify_identity || null;
                const signature =
                    campaign?.campaignApplication?.mp_signature || '';
                setConsent(consent);
                setCertifyIdentity(certifyIdentity);
                setSignature(signature);
                initializeData(medProf);
            } else {
                setLoading(false);
                setLoadingOptions(false);
            }
        } else {
            getCampaign();
        }
    }, [campaign]);

    const getMedicalCenters = async () => {
        setLoadingOptions(true);
        const resp = await searchMedicalCenters(state);
        const opts = resp.map((medCenter: any) => {
            return {
                label: medCenter.name,
                value: medCenter.id,
            };
        });
        setMedicalCenterOptions(opts);
        setLoadingOptions(false);
    };

    useEffect(() => {
        if (state && !loadingOptions) {
            getMedicalCenters();
        }
    }, [state]);

    const getMedicalProfessionals = async () => {
        setLoadingOptions(true);
        const medCenterId = selectedMedicalCenter?.value;
        const resp = await searchMedicalProfessionals(medCenterId);
        const opts = resp.map((prof: any) => {
            const label = `${prof.first_name} ${prof.last_name}`;
            return {
                label,
                value: prof.id,
            };
        });
        setMedicalProfessionalOptions(opts);
        setLoadingOptions(false);
    };

    useEffect(() => {
        if (selectedMedicalCenter && !loadingOptions) {
            getMedicalProfessionals();
        }
    }, [selectedMedicalCenter]);

    const handleNext = async () => {
        setSaving(true);
        toast.loading('Saving Medical Professional information...');
        try {
            const medCenterId = selectedMedicalCenter?.value;
            const personId = selectedMedicalProfessional?.value;
            const resp = await setMedicalProfessional(
                medCenterId,
                personId,
                consent,
                certifyIdentity,
                signature,
                campaignId
            );
            toast.dismiss();
            
            if (resp.detail) {
                // API returned an error
                toast.error(resp.detail);
                setSaving(false);
                return;
            }
            
            campaignDispatch({
                type: 'set_campaign',
                payload: resp,
            });
            toast.success('Medical Professional information saved!');
            history.push('/dashboard');
        } catch (e) {
            toast.dismiss();
            console.error('Error saving medical professional:', e);
            toast.error(
                'There was an error saving your Medical Professional information'
            );
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return <Loading />;
    }

    const isValid =
        selectedMedicalCenter &&
        selectedMedicalProfessional &&
        consent &&
        certifyIdentity &&
        signature &&
        !saving;

    return (
        <div className='container__inner' style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', marginBottom: 0, paddingBottom: 0 }}>
            <div style={{ flexGrow: 1 }}>
            <Row>
                <Col xl={6}>
                    <div>
                        <h3>Medical Professional</h3>
                        <p>
                            To certify medical need, we need confirmation of
                            your status from a medical provider, such as a
                            social worker, financial coordinator, case manager,
                            or rehabilitation specialist.
                        </p>
                    </div>

                    <Form>
                        <FormGroup>
                            <Label>
                                Where is your Medical Professional located?
                            </Label>
                            <Select
                                styles={colourStyles}
                                components={{ DropdownIndicator }}
                                options={stateOptions}
                                onChange={(opt) => {
                                    setState((opt as SelectOption)?.label || '');
                                }}
                                value={
                                    state &&
                                    stateOptions.find((x) => x.label === state)
                                }
                            />
                            <FormFeedback>Please select a state</FormFeedback>
                        </FormGroup>
                        {((state &&
                            medicalCenterOptions &&
                            !showMedCenterForm) ||
                            showMedProfForm) && (
                            <>
                                <FormGroup>
                                    <Label>
                                        What is your Medical Professional's
                                        medical center?
                                    </Label>
                                    <Select
                                        styles={colourStyles}
                                        components={{ DropdownIndicator }}
                                        options={medicalCenterOptions}
                                        onChange={(opt) => {
                                            setSelectedMedicalCenter(opt as SelectOption | null);
                                        }}
                                        value={
                                            selectedMedicalCenter &&
                                            medicalCenterOptions.find(
                                                (x) =>
                                                    x.value ===
                                                    selectedMedicalCenter.value
                                            )
                                        }
                                    />
                                </FormGroup>
                            </>
                        )}
                        {state && !showMedCenterForm && !showMedProfForm && (
                            <p className='small'>
                                Is your Medical Center not listed here?{' '}
                                <a
                                    href='#'
                                    onClick={(e) => {
                                        e.preventDefault();
                                        setShowMedCenterForm(true);
                                    }}
                                >
                                    Click here to add it.
                                </a>
                            </p>
                        )}

                        {showMedCenterForm && (
                            <MedCenterForm
                                location={state}
                                onCancel={() => setShowMedCenterForm(false)}
                            />
                        )}

                        {state &&
                            selectedMedicalCenter &&
                            medicalProfessionalOptions &&
                            !showMedCenterForm &&
                            !showMedProfForm && (
                                <FormGroup>
                                    <Label>
                                        Who is your Medical Professional?
                                    </Label>
                                    <Select
                                        styles={colourStyles}
                                        components={{ DropdownIndicator }}
                                        options={medicalProfessionalOptions}
                                        onChange={(opt) => {
                                            setSelectedMedicalProfessional(opt as SelectOption | null);
                                        }}
                                        value={selectedMedicalProfessional}
                                    />
                                </FormGroup>
                            )}

                        {state &&
                            !showMedCenterForm &&
                            !showMedProfForm &&
                            selectedMedicalCenter && (
                                <p className='small'>
                                    Is your Medical Professional not listed
                                    here?{' '}
                                    <a
                                        href='#'
                                        onClick={(e) => {
                                            e.preventDefault();
                                            setShowMedProfForm(true);
                                        }}
                                    >
                                        Click here to add it.
                                    </a>
                                </p>
                            )}

                        {showMedProfForm && (
                            <MedCenterForm
                                location={state}
                                selectedMedicalCenter={selectedMedicalCenter}
                                onCancel={() => setShowMedProfForm(false)}
                                profOnly={true}
                            />
                        )}

                        <Row>
                            <Col>
                                {loadingOptions && (
                                    <Spinner color='primary' size='sm'>
                                        Loading
                                    </Spinner>
                                )}
                                {loadingOptions && <div>Loading</div>}
                            </Col>
                        </Row>
                        {!showMedCenterForm && !showMedProfForm && (
                            <>
                                <FormGroup check className='mb-3 ps-0'>
                                    <CheckBox
                                        name='consent'
                                        label='I give consent to their health care professional to provide information to Help Hope Live confirming their medical and financial need for the purpose of fundraising.'
                                        value={consent}
                                        onChange={() => setConsent(!consent)}
                                    />
                                </FormGroup>

                                <Row>
                                    <Col sm={6}>
                                        <FormGroup>
                                            <Label>
                                                I certify that I am the:
                                            </Label>
                                            <Select
                                                styles={colourStyles}
                                                components={{
                                                    DropdownIndicator,
                                                }}
                                                options={identityOptions}
                                                onChange={(opt) => {
                                                    const selectedOpt = opt as SelectOption | null;
                                                    setCertifyIdentity(
                                                        selectedOpt?.value ? Number(selectedOpt.value) : null
                                                    );
                                                }}
                                                value={
                                                    certifyIdentity &&
                                                    identityOptions.find(
                                                        (x) =>
                                                            x.value ===
                                                            certifyIdentity
                                                    )
                                                }
                                            />
                                            <FormFeedback invalid>
                                                Please select an option
                                            </FormFeedback>
                                        </FormGroup>
                                    </Col>

                                    <Col sm={6}>
                                        <FormGroup>
                                            <Label>
                                                Name of individual saving these
                                                agreements
                                            </Label>
                                            <Input
                                                type='text'
                                                name='mp_signature'
                                                id='mp_signature'
                                                onChange={(e) => {
                                                    setSignature(
                                                        e.target.value
                                                    );
                                                }}
                                                value={signature}
                                                readOnly={readOnly}
                                                valid={signature.length > 0}
                                                invalid={!signature}
                                            />
                                            <FormFeedback>
                                                Your signature is required.
                                            </FormFeedback>
                                        </FormGroup>
                                    </Col>
                                </Row>
                            </>
                        )}
                    </Form>
                </Col>
            </Row>
            </div>
            {!showMedCenterForm && !showMedProfForm && (
                <div
                    className='sticky-bottom p-4 bg-white mb-0'
                    style={{
                        borderTop: '1px solid #0D0106',
                        boxShadow: '0px 4px 16px rgba(0, 0, 0, 0.25)',
                        marginBottom: 0,
                    }}
                >
                    <Button
                        className='mb-0'
                        color='outline-dark'
                        disabled={saving}
                        onClick={() => history.push('/patient/edit')}
                        style={{ marginBottom: 0 }}
                    >
                        Cancel
                    </Button>
                    {!readOnly && (
                        <Button
                            className='mb-0'
                            color='primary'
                            disabled={!isValid || saving}
                            onClick={handleNext}
                            style={{ marginBottom: 0 }}
                        >
                            {saving ? `Saving` : `Save`}
                        </Button>
                    )}
                </div>
            )}
        </div>
    );
};
